import { useState, useEffect, FC } from "react";
import Image from "next/legacy/image";
import { P1 } from "../atoms/Paragraph";
import styled from "styled-components";
import { Checkbox } from "../atoms/Checkbox";
import { color as themeColor } from "@styles";
import { isEmail, getCookie, parseQuery, config } from "@utils";
import { HubspotForms } from "@utils";
import { H3 } from "../atoms/Heading";
import { GiraffeHeadsetAvatar } from "../atoms/svgs/GiraffeHeadsetAvatar";
import { Button } from "../atoms/Button";
import Bugsnag from "@bugsnag/js";
import DropdownIcon from "../../public/static/icons/DropdownIcon.svg";
import {
  GET_A_CALLBACK_FORM_ID,
  GET_A_CALLBACK_SUBMIT_ID,
  GET_A_QUOTE_FORM_ID,
  GET_A_QUOTE_SUBMIT_ID,
} from "utils/_constants";
import { ApiRoutes } from "@routes";
import { getHubspotFieldsFromSearchQuery } from "utils/form";
import { useHubspotForm } from "hooks/useHubspotForm";

const HubspotPropertiesMap = {
  quoteType: "what_would_you_like_to_get_a_quote_for_",
  lookingToContact: "who_would_you_like_to_speak_to_",
  firstName: "firstname",
  lastName: "lastname",
  company: "company",
  jobTitle: "jobtitle",
  email: "email",
  phone: "phone",
  numberOfEmployees: "number_of_employees_string",
  hasGroupLife: "do_you_already_have_group_life_",
};

export enum FormType {
  callback = "Callback",
  quote = "Quote",
}

enum QuoteType {
  GLI = "Group Life Insurance",
  GCI = "Group Critical Illness",
  GIP = "Group Income Protection",
}

enum Page {
  companyDetails,
  personalDetails,
  success,
  instantDirect,
}

const quoteTypeArray = [QuoteType.GLI, QuoteType.GCI, QuoteType.GIP];

enum LookingToContactType {
  question = "",
  sales = "Sales",
  customerSupport = "Customer Support",
  partnerships = "Partnerships",
  pressEnquiries = "Press Enquiries",
  other = "Other",
}

const LookingToContactArray = [
  LookingToContactType.question,
  LookingToContactType.sales,
  LookingToContactType.customerSupport,
  LookingToContactType.partnerships,
  LookingToContactType.pressEnquiries,
  LookingToContactType.other,
];

enum CompanySize {
  question = "",
  A = "1 - 2",
  B = "3 - 10",
  C = "11 - 50",
  D = "51 - 200",
  E = "201 - 500",
  F = "501 - 2500",
  G = "2500+",
}

const companySizeArray = [
  CompanySize.question,
  CompanySize.A,
  CompanySize.B,
  CompanySize.C,
  CompanySize.D,
  CompanySize.E,
  CompanySize.F,
  CompanySize.G,
];

export const QuoteForm: FC<
  React.PropsWithChildren<{
    formType?: FormType;
    close?: () => void;
    sendMessage?: () => void;
    maxWidth?: string;
    theme?: "purple" | "grey";
  }>
> = ({ formType, close, sendMessage, maxWidth = "42rem", theme = "purple" }) => {
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");
  const [isShaking, setIsShaking] = useState(false);
  const [state, setState] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    quoteType: "",
    lookingToContact: LookingToContactType.question,
    company: "",
    jobTitle: "",
    numberOfEmployees: CompanySize.question,
    hasGroupLife: "No" as "Yes" | "No",
  });
  const [searchQuery, setSearchQuery] = useState("");

  const { handleFormSubmit } = useHubspotForm();

  useEffect(() => {
    if (sendMessage) {
      sendMessage();
      window.addEventListener("message", (event: MessageEvent) => {
        // if the parent window sends a message with a search string, update the state
        if (event.data.hasOwnProperty("search")) {
          setSearchQuery(event.data.search);
        }
      });
    }
  }, [page, sendMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.target.getAttribute("name") === "hasGroupLife") {
      const isChecked = (e.target as any).checked;
      const value = isChecked ? "Yes" : "No";
      setState({ ...state, hasGroupLife: value });
    } else {
      setState({ ...state, [e.target.getAttribute("name")]: e.target.value });
    }
  };

  const validatePersonalDetails = (): string => {
    const { firstName, lastName, phone } = state;

    let error: string;

    if (!firstName || !lastName || !phone) {
      error = "Please fill in the required fields";
    } else {
      error = "";
      setError("");
    }

    return error;
  };

  const validateCompanyDetails = (): string => {
    const { company, numberOfEmployees, quoteType, email } = state;
    let error: string;

    if (!company) {
      error = "Please enter the name of your company";
    } else if (!numberOfEmployees) {
      error = "Please select the number of employees";
    } else if (formType === FormType.quote && !quoteType) {
      error = "Please select the type of quote you're interested in";
    } else if (!isEmail(email)) {
      error = "Please enter a valid email address";
    } else {
      error = "";
      setError("");
    }

    return error;
  };

  const submitCompanyDetails = (e: React.MouseEvent<HTMLInputElement>) => {
    const error = validateCompanyDetails();

    if (error) {
      setError(error);
      setIsShaking(true);

      setTimeout(() => {
        setIsShaking(false);
      }, 820);
    } else {
      setPage(Page.personalDetails);
    }
  };

  const submitPersonalDetails = async (e: React.MouseEvent<HTMLInputElement>) => {
    const error = validatePersonalDetails();

    if (error) {
      setError(error);
      setIsShaking(true);

      setTimeout(() => {
        setIsShaking(false);
      }, 820);
    } else {
      const isSuccessful = await sendFormToHubspot();
      if (isSuccessful) {
        setPage(Page.success);
      }
    }
  };

  const sendFormToHubspot = async () => {
    let name: HubspotForms;

    if (formType === FormType.quote) {
      name = HubspotForms.quote;
    } else {
      name = HubspotForms.callback;
    }

    if (!name) {
      return;
    }

    const fields = Object.entries(state)
      .map(([fieldName, value]) => {
        return {
          name: HubspotPropertiesMap[fieldName],
          value,
        };
      })
      .filter((field) => {
        if (field.name === undefined) {
          return false;
        }

        if (field.name === "do_you_already_have_group_life_") {
          return (
            (name === HubspotForms.quote && state.quoteType.includes(QuoteType.GLI)) ||
            name === HubspotForms.callback
          );
        }

        if (field.name === "who_would_you_like_to_speak_to_") {
          return name === HubspotForms.callback;
        }

        return true;
      });

    const hutk = getCookie("hubspotutk");

    // by now, the parent window should have received a message containing a search string
    // if not, we can try 2 fallback methods
    let parentWindowQuery = "";
    let windowQuery = "";

    // using a try/catch block in case of CORS error
    try {
      parentWindowQuery =
        window && window.parent && window.parent.location && window.parent.location.search;
      windowQuery = window && window.location && window.location.search;
    } catch (e) {
      Bugsnag.notify(e);
    }

    const query = parseQuery(searchQuery || parentWindowQuery || windowQuery);

    fields.push(...getHubspotFieldsFromSearchQuery(query));

    let href: string;
    if (window && window.location && window.location.href) {
      ({ href } = window.location);
    }

    const formData = {
      fields,
      context: {
        hutk,
        pageUri: href || "https://yulife.com/contact-us/",
        pageName: "Contact Us",
      },
    };

    try {
      await handleFormSubmit(name, formData);

      return true;
    } catch (e) {
      Bugsnag.notify(e);
      return false;
    }
  };

  const PersonalDetails = (
    <Fieldset>
      <legend>Personal Details:</legend>
      <InputRow>
        <InputContainer>
          <Input
            onChange={handleChange}
            value={state.firstName}
            required
            name="firstName"
            id="firstName"
            type="text"
          />
          <InputLabelWrapper invisible={!!state.firstName}>
            <InputLabel htmlFor="firstName">
              First name
              <Asterisk />
            </InputLabel>
          </InputLabelWrapper>
        </InputContainer>
        <InputContainer>
          <Input
            onChange={handleChange}
            value={state.lastName}
            required
            name="lastName"
            id="lastName"
            type="text"
          />
          <InputLabelWrapper invisible={!!state.lastName}>
            <InputLabel htmlFor="lastName">
              Last name
              <Asterisk />
            </InputLabel>
          </InputLabelWrapper>
        </InputContainer>
      </InputRow>
      <InputContainer>
        <Input
          onChange={handleChange}
          value={state.phone}
          required
          name="phone"
          id="phone"
          type="phone"
        />
        <InputLabelWrapper invisible={!!state.phone}>
          <InputLabel htmlFor="phone">
            Phone number
            <Asterisk />
          </InputLabel>
        </InputLabelWrapper>
      </InputContainer>
      <InputContainer>
        <Input
          onChange={handleChange}
          value={state.jobTitle}
          name="jobTitle"
          id="jobTitle"
          type="text"
        />
        <InputLabelWrapper invisible={!!state.jobTitle}>
          <InputLabel htmlFor="jobTitle">Job title</InputLabel>
        </InputLabelWrapper>
      </InputContainer>
      <Submit
        onClick={submitPersonalDetails}
        isInputValid={!isShaking}
        type="submit"
        value="Submit"
        id={formType === FormType.quote ? GET_A_QUOTE_SUBMIT_ID : GET_A_CALLBACK_SUBMIT_ID}
      />
      <div style={{ textAlign: "right" }}>
        <P1 color="#A0A09B" pt="1rem">
          {error ? (
            <>
              <span style={{ color: "#ff6666" }}>Error: </span>
              {error}
            </>
          ) : (
            <>
              <Asterisk />
              Required fields
            </>
          )}
        </P1>
      </div>
      <Centered>
        <Circle onClick={() => setPage(0)} />
        <Circle current />
      </Centered>
    </Fieldset>
  );

  const LookingToContactSelect = (
    <InputContainer>
      <ArrowContainer>
        <Image src={DropdownIcon} alt="" />
      </ArrowContainer>
      <Select
        onChange={handleChange}
        value={state.lookingToContact}
        required
        name="lookingToContact"
        id="lookingToContact"
        $isPlaceholder={state.lookingToContact === LookingToContactType.question}
      >
        {LookingToContactArray.map((inquiry, index) => (
          <option key={inquiry} value={inquiry}>
            {inquiry}
          </option>
        ))}
      </Select>
      <InputLabelWrapper invisible={state.lookingToContact !== LookingToContactType.question}>
        <InputLabel
          htmlFor="lookingToContact"
          invisible={state.lookingToContact !== LookingToContactType.question}
        >
          Who would you like to speak to?
        </InputLabel>
      </InputLabelWrapper>
    </InputContainer>
  );

  const QuoteTypeSelect = (
    <InputContainer>
      <Checkbox
        question="What type of quote do you want?"
        values={quoteTypeArray}
        checkedValues={state.quoteType.split(", ")}
        handleChange={(checkedValues: string[]) => {
          setState({
            ...state,
            quoteType: checkedValues.join(", "),
          });
          if (sendMessage) {
            setTimeout(() => {
              sendMessage();
            }, 50);
          }
        }}
      />
    </InputContainer>
  );

  const CompanyDetails = (
    <Fieldset>
      <legend>Company Details</legend>
      {formType === FormType.quote ? QuoteTypeSelect : LookingToContactSelect}
      <InputRow>
        <InputContainer>
          <Input
            onChange={handleChange}
            value={state.company}
            required
            name="company"
            id="company"
            type="text"
          />
          <InputLabelWrapper invisible={!!state.company}>
            <InputLabel htmlFor="company">
              Company name
              <Asterisk />
            </InputLabel>
          </InputLabelWrapper>
        </InputContainer>
        <InputContainer>
          <Input
            onChange={handleChange}
            value={state.email}
            name="email"
            id="email"
            type="email"
            required
          />
          <InputLabelWrapper invisible={!!state.email}>
            <InputLabel htmlFor="email">
              Work Email
              <Asterisk />
            </InputLabel>
          </InputLabelWrapper>
        </InputContainer>
      </InputRow>
      <InputContainer>
        <ArrowContainer>
          <Image src={DropdownIcon} alt="" />
        </ArrowContainer>
        <Select
          onChange={handleChange}
          value={state.numberOfEmployees}
          required
          name="numberOfEmployees"
          id="numberOfEmployees"
          $isPlaceholder={state.numberOfEmployees === CompanySize.question}
        >
          {companySizeArray.map((inquiry, index) => (
            <option key={inquiry} value={inquiry}>
              {inquiry}
            </option>
          ))}
        </Select>
        <InputLabelWrapper invisible={state.numberOfEmployees !== CompanySize.question}>
          <InputLabel
            htmlFor="numberOfEmployees"
            invisible={state.numberOfEmployees !== CompanySize.question}
          >
            Number of employees
            <Asterisk />
          </InputLabel>
        </InputLabelWrapper>
      </InputContainer>
      {formType === FormType.callback ||
      (formType === FormType.quote && state.quoteType.includes(QuoteType.GLI)) ? (
        <div style={{ display: "flex", alignItems: "center" }}>
          <InputContainer>
            <Checkbox
              values={["I already have group life"]}
              checkedValues={state.hasGroupLife === "Yes" ? ["I already have group life"] : []}
              handleChange={(checkedValues: string[]) => {
                setState({
                  ...state,
                  hasGroupLife: checkedValues.length ? "Yes" : "No",
                });
              }}
            />
          </InputContainer>
        </div>
      ) : null}
      <Submit
        isInputValid={!isShaking}
        onClick={(e) => {
          submitCompanyDetails(e);
        }}
        type="button"
        value="Continue"
      />
      <div style={{ textAlign: "right" }}>
        <P1 color="#A0A09B">
          {error ? (
            <>
              <span style={{ color: "#ff6666" }}>Error: </span>
              {error}
            </>
          ) : (
            <>
              <Asterisk />
              Required fields
            </>
          )}
        </P1>
      </div>
      <Centered>
        <Circle current />
        <Circle onClick={submitCompanyDetails} />
      </Centered>
    </Fieldset>
  );

  const Success = (
    <>
      <SuccessSection>
        <H3 pb="2rem" align="center">
          We’re on our way!
        </H3>
        <GiraffeHeadsetAvatar />
        <P1 pt="2rem">
          A member of our team will get back to you as soon as possible
          {formType === FormType.quote ? " to discuss your requirements." : "."}
        </P1>
        {formType === FormType.callback || !close ? null : (
          <P1>
            <Anchor onClick={close}>Close this</Anchor>
          </P1>
        )}
      </SuccessSection>
    </>
  );

  const InstantDirect = (
    <>
      <SuccessSection>
        <GiraffeHeadsetAvatar width="98" height="98" />
        <H3 pt="2rem" align="center">
          You can get a quote instantly, 100% online
        </H3>
        <P1>
          Our Instant Group life insurance provides the same level of cover for every employee in
          your organisation. You can get everyone covered within 24 hours.
        </P1>
        <A target="_blank" href="/aig">
          <P1 pt="0" color="#E30D76" underlined>
            How we work with AIG
          </P1>
        </A>
        <A target="_blank" href="/static/docs/instant/member-guide/1.0.0/member-guide.pdf">
          <P1 pt="0" color="#E30D76" underlined>
            Member Guide
          </P1>
        </A>
        <A target="_blank" href="/static/docs/instant/technical-guide/1.0.0/technical-guide.pdf">
          <P1 pt="0" color="#E30D76" underlined>
            Technical Guide
          </P1>
        </A>
        <A
          target="_blank"
          href="/static/docs/instant/terms-and-conditions/1.0.0/terms-and-conditions.pdf"
        >
          <P1 pt="0" color="#E30D76" underlined>
            Terms and Conditions
          </P1>
        </A>
        <Button
          style={{ margin: "2rem 0" }}
          minwidth="100%"
          url={`${config.TEAM_URL}/group/signup`}
        >
          Get an online quote
        </Button>
        <P1 pt="0">
          <Anchor onClick={() => setPage(Page.companyDetails)}>Go back</Anchor>
        </P1>
      </SuccessSection>
    </>
  );

  const pages = [CompanyDetails, PersonalDetails, Success, InstantDirect];

  const Form = (
    <FormContainer
      maxWidth={maxWidth}
      theme={theme}
      id={formType === FormType.quote ? GET_A_QUOTE_FORM_ID : GET_A_CALLBACK_FORM_ID}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      {pages[page]}
    </FormContainer>
  );

  return Form;
};

const Asterisk = styled.span`
  &:before {
    content: "*";
    color: #ff6666;
  }
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
`;

const Circle = styled.div<{ current?: boolean }>`
  cursor: pointer;
  height: 1rem;
  width: 1rem;
  background: ${(p) => (p.current ? "#956AFF" : "#DFD3FF")};
  border-radius: 50%;
  display: inline-block;
  margin: 1rem 0.5rem 1rem 0.5rem;
`;

const FormContainer = styled.form<{ maxWidth: string; theme: "purple" | "grey" }>`
  background: #fafafe;
  width: 100%;
  max-width: ${(p) => p.maxWidth || "42rem"};
  height: auto;
  margin: 0 auto 2rem auto;
  padding: 2.5rem;
  border: 2px solid ${(p) => (p.theme === "grey" ? "#D9D9D7" : "#c7b4fd")};
  box-shadow: 8px 8px 0px ${(p) => (p.theme === "grey" ? "rgba(160, 160, 155, 0.24)" : "#eae1ff")};
  border-radius: 8px;

  @media only screen and (max-width: 60rem) {
    padding: 2rem;
  }

  @media only screen and (max-width: 30rem) {
    padding: 1rem;
    max-width: 100%;
    overflow-x: hidden;
  }
`;

const InputContainer = styled.div`
  position: relative;
`;

const Fieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;

  input[required]:valid + label {
    display: none;
  }

  legend {
    position: absolute;
    left: -10000px;
    top: auto;
  }
`;

const InputLabelWrapper = styled.div<{ invisible?: boolean }>`
  width: 100%;
  max-width: calc(100% - 4.5rem);
  min-height: 3.25rem;
  position: absolute;
  top: 1.4rem;
  left: 1.375rem;
  display: flex;
  align-items: center;
  pointer-events: none;
  ${(p) => (p.invisible ? "visibility: hidden" : null)};
`;

const InputLabel = styled.label<{ invisible?: boolean }>`
  ${(p) => (p.invisible ? "visibility: hidden" : null)};
  color: #a0a09b;
  font-weight: 400;
  font-size: 1.25rem;
  pointer-events: none;
`;

const Input = styled.input`
  width: 100%;
  margin: 1rem 0;
  box-sizing: border-box;
  padding: 1rem 1.25rem;
  font-weight: 400;
  font-size: 1.25rem;
  color: ${themeColor.B1100};
  border-radius: 0.5rem;
  border: 2px solid #d9d9d7;

  /* Placeholder Colours */
  ::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    color: #a0a09b;
  }
  ::-moz-placeholder {
    /* Firefox 19+ */
    color: #a0a09b;
  }
  :-ms-input-placeholder {
    /* IE 10+ */
    color: #a0a09b;
  }
  :-moz-placeholder {
    /* Firefox 18- */
    color: #a0a09b;
  }
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1rem;

  @media only screen and (max-width: 60rem) {
    grid-template-columns: 1fr;
  }
`;

const Submit = styled.input<{ isInputValid?: boolean }>`
  width: 100%;
  margin: 1rem 0;
  border: 2px solid #00ed9d;
  box-sizing: border-box;
  padding: 1rem 1.25rem;
  font-weight: bold;
  font-size: 1.25rem;
  color: ${themeColor.B1100};
  background: #00ed9d;
  border-radius: 0.5rem;
  color: #fff;
  cursor: pointer;

  ${(p) =>
    p.isInputValid ? null : `animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both`};
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  perspective: 1000px;

  @keyframes shake {
    10%,
    90% {
      transform: translate3d(-1px, 0, 0);
    }

    20%,
    80% {
      transform: translate3d(2px, 0, 0);
    }

    30%,
    50%,
    70% {
      transform: translate3d(-4px, 0, 0);
    }

    40%,
    60% {
      transform: translate3d(4px, 0, 0);
    }
  }
`;

const Select = styled.select<{ $isPlaceholder?: boolean }>`
  width: 100%;
  margin: 1rem 0;
  box-sizing: border-box;
  padding: 1rem 1.25rem;
  font-weight: 400;
  font-size: 1.25rem;
  color: ${(p) => (p.$isPlaceholder ? "#A0A09B" : themeColor.B1100)};
  border-radius: 0.5rem;
  border: 0.125rem solid #d9d9d7;
  appearance: none;
`;

const ArrowContainer = styled.div`
  position: absolute;
  right: 0;
  height: 5.75rem;
  padding: 1rem 1.25rem;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const SuccessSection = styled.div`
  text-align: center;
`;

const Anchor = styled.a`
  text-decoration: underline;
  color: ${themeColor.P100};

  ::-moz-selection {
    background: ${themeColor.P20};
  }
  ::selection {
    background: ${themeColor.P20};
  }
`;

const A = styled.a`
  display: block;
  margin-top: 1rem;
`;
